import type {
  GenerationContext,
  GenerationCounts,
  GenerationDetail,
  GenerationEvent,
  GenerationExample,
  GenerationItem,
  GenerationItemKind,
  GenerationItemStatus,
  GenerationKind,
  GenerationMarks,
  GenerationMeaning,
  GenerationPick,
  GenerationProblem,
  GenerationSense,
  GenerationSenseStatus,
  GenerationStatus,
  GenerationSummary,
  GenerationsPage,
} from '../model/types';

/**
 * Один разборщик генераций — на snake_case (ТЗ главной § 22.9): REST после интерцептора axios
 * уже в snake_case, SSE `generationUpdated` пропускают через snakeizeKeys ещё в потоке
 * уведомлений. Неполные и неизвестные значения не роняют разбор: пустые списки, null и
 * нейтральные статусы. Без alias-импортов — модуль собирают тесты.
 */

type Raw = Record<string, unknown>;

const obj = (value: unknown): Raw | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : null;
const str = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);
const num = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
const strList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string') : [];
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

const KINDS: readonly GenerationKind[] = ['word', 'topic', 'manual'];
const STATUSES: readonly GenerationStatus[] = ['generating', 'ready', 'partial', 'failed', 'removed'];
const ITEM_KINDS: readonly GenerationItemKind[] = ['word', 'phrase', 'phrasal_verb'];
const ITEM_STATUSES: readonly GenerationItemStatus[] = [
  'generating',
  'extending',
  'ready',
  'selection_failed',
  'failed',
  'typo',
  'garbage',
  'removed',
  'skipped',
];
const SENSE_STATUSES: readonly GenerationSenseStatus[] = ['ready', 'generating', 'failed', 'removed'];

function parseCounts(raw: unknown): GenerationCounts {
  const c = obj(raw) ?? {};
  return {
    total: num(c.total),
    ready: num(c.ready),
    generating: num(c.generating),
    failed: num(c.failed),
    removed: num(c.removed),
    skipped: num(c.skipped),
    words: num(c.words),
    phrases: num(c.phrases),
    phrasalVerbs: num(c.phrasal_verbs),
    meanings: num(c.meanings),
    picks: num(c.picks),
    failedPicks: num(c.failed_picks),
  };
}

/** Сводка записи; без id — null. */
export function parseGenerationSummary(raw: unknown): GenerationSummary | null {
  const r = obj(raw);
  const id = r ? text(r.id) : null;
  if (!r || !id) return null;
  const module = obj(r.module);
  const moduleId = module ? text(module.id) : null;
  const tokens = obj(r.tokens) ?? {};
  const createdAt = str(r.created_at) ?? '';
  const computed = text(r.computed_at);
  const computedAt = computed ? Date.parse(computed) : NaN;
  return {
    id,
    kind: oneOf(r.kind, KINDS, 'word'),
    title: str(r.title) ?? '',
    module: module && moduleId ? { id: moduleId, name: str(module.name) ?? '' } : null,
    topicBatchId: text(r.topic_batch_id),
    createdAt,
    updatedAt: str(r.updated_at) ?? createdAt,
    computedAt: Number.isNaN(computedAt) ? null : computedAt,
    // Неизвестный статус — нейтральный «готово»: «генерируется» держал бы опрос вечно.
    status: oneOf(r.status, STATUSES, 'ready'),
    processing: r.processing === true,
    fronts: strList(r.fronts),
    counts: parseCounts(r.counts),
    tokens: { held: num(tokens.held), refunded: num(tokens.refunded) },
  };
}

function parsePick(raw: unknown): GenerationPick | null {
  const p = obj(raw);
  const translation = p ? text(p.translation) : null;
  return p && translation ? { pos: str(p.pos), translation } : null;
}

const picks = (value: unknown): GenerationPick[] =>
  list(value)
    .map(parsePick)
    .filter((p): p is GenerationPick => p !== null);

function parseExample(raw: unknown): GenerationExample | null {
  const e = obj(raw);
  const en = e ? text(e.en) : null;
  return e && en ? { en, ru: text(e.ru) } : null;
}

function parseMarks(raw: unknown): GenerationMarks {
  const m = obj(raw) ?? {};
  return {
    registers: strList(m.registers),
    region: strList(m.region),
    grammar: strList(m.grammar),
    usageNote: text(m.usage_note),
    frequency: text(m.frequency),
    cefr: text(m.cefr),
  };
}

function parseContext(raw: unknown): GenerationContext | null {
  const c = obj(raw);
  if (!c) return null;
  return {
    exampleEn: text(c.example_en),
    simpleExampleEn: text(c.simple_example_en),
    simpleExampleRu: text(c.simple_example_ru),
    hint: text(c.hint),
    definition: text(c.definition),
  };
}

function parseSense(raw: unknown): GenerationSense | null {
  const s = obj(raw);
  const pick = s ? parsePick(s.pick) : null;
  if (!s || !pick) return null;
  const translations = strList(s.translations).filter((t) => t.trim());
  return {
    pick,
    status: oneOf(s.status, SENSE_STATUSES, 'ready'),
    senseId: text(s.sense_id),
    translations: translations.length ? translations : [pick.translation],
    definition: text(s.definition),
    hint: text(s.hint),
    simpleExample: parseExample(s.simple_example),
    example: parseExample(s.example),
    clozeExample: parseExample(s.cloze_example),
    contexts: list(s.contexts)
      .map(parseContext)
      .filter((c): c is GenerationContext => c !== null),
    enSynonyms: strList(s.en_synonyms),
    marks: parseMarks(s.marks),
  };
}

function parseMeaning(raw: unknown): GenerationMeaning | null {
  const m = obj(raw);
  if (!m) return null;
  return {
    pos: str(m.pos),
    rareTranslations: strList(m.rare_translations),
    senses: list(m.senses)
      .map(parseSense)
      .filter((s): s is GenerationSense => s !== null),
  };
}

function parseProblem(raw: unknown): GenerationProblem | null {
  const p = obj(raw);
  if (!p) return null;
  return { code: text(p.code) ?? 'failed', message: text(p.message), suggestions: strList(p.suggestions) };
}

function parseItem(raw: unknown, index: number): GenerationItem | null {
  const i = obj(raw);
  const frontName = i ? text(i.front_name) : null;
  if (!i || !frontName) return null;
  const removal = i.removal === 'deleted' || i.removal === 'hidden' ? i.removal : null;
  const token = i.token === 'held' || i.token === 'refunded' ? i.token : null;
  return {
    id: text(i.id) ?? `${index}:${frontName}`,
    cardId: text(i.card_id),
    kind: oneOf(i.kind, ITEM_KINDS, 'word'),
    frontName,
    createdAt: str(i.created_at) ?? '',
    transcription: text(i.transcription),
    isPhrasalVerb: i.is_phrasal_verb === true,
    manual: i.manual === true,
    status: oneOf(i.status, ITEM_STATUSES, 'ready'),
    removal,
    skipReason: text(i.skip_reason),
    problem: parseProblem(i.problem),
    token,
    failedPicks: picks(i.failed_picks),
    meanings: list(i.meanings)
      .map(parseMeaning)
      .filter((m): m is GenerationMeaning => m !== null),
  };
}

/** Деталь записи: сводка и все элементы по порядку, включая пропуски. */
export function parseGenerationDetail(raw: unknown): GenerationDetail | null {
  const summary = parseGenerationSummary(raw);
  const r = obj(raw);
  if (!summary || !r) return null;
  return {
    ...summary,
    items: list(r.items)
      .map(parseItem)
      .filter((item): item is GenerationItem => item !== null),
  };
}

/** Страница списка `GET /generations`: новые сверху, курсор следующей или null. */
export function parseGenerationsPage(raw: unknown): GenerationsPage {
  const r = obj(raw) ?? {};
  return {
    items: list(r.items)
      .map(parseGenerationSummary)
      .filter((s): s is GenerationSummary => s !== null),
    nextCursor: text(r.next_cursor),
  };
}

/** SSE `generationUpdated` (уже через snakeizeKeys): `{generation, card_id}`. */
export function parseGenerationEvent(raw: unknown): GenerationEvent | null {
  const r = obj(raw);
  const generation = r ? parseGenerationSummary(r.generation) : null;
  return r && generation ? { generation, cardId: text(r.card_id) } : null;
}
